var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { appointmentKindLabels } from 'constants/appointmentKind';
import { defaultPageSizeOptions } from 'constants/table';
import { differenceInDays } from 'date-fns';
import { NavLink } from 'react-router-dom';
import { Info } from 'shared/assets/icons';
import { NoAppointments } from 'shared/assets/icons/noAppointments';
import { Text, Box, NoData, VBadge, Flex, HStack, Spinner } from 'shared/components';
import { RiskTableCell, StatusTable, TableCellLink } from 'shared/features/table';
import { findLastAppointment } from 'shared/hooks';
import { formatCalendarDate, formatFullDate, formatFullDateTime, getAgeFromFormattedDob, linkToMemberMeasures, linkToNote, linkToNoteOrAppointment, } from 'shared/utils';
import { AssessmentPositiveNegativeKinds } from 'src/constants/assessments';
import { useFlagEnabled } from 'src/flagging/hooks';
import { assessmentRiskRender } from 'src/shared/features/assessments';
import { useAppointmentsMeasures } from 'src/shared/features/mbc';
import { useFlaggedRiskConcerns, useFlaggedUpcomingAppointmentsAndNotes, } from 'src/shared/hooks/useFlaggedAppointmentsAndNotes';
/**
 * The table for the upcoming notes
 *
 * @returns {React.ReactNode}
 */
var Upcoming = function () {
    var _a = useFlaggedUpcomingAppointmentsAndNotes(), apptData = _a.apptData, appointments = _a.appointments, notesIndexedByAppointmentId = _a.notesIndexedByAppointmentId, appointmentIndex = _a.appointmentIndex, isLoading = _a.isLoading, isError = _a.isError;
    var measuresContentFlagEnabled = useFlagEnabled('ENABLE_MEASURES_MBC_UPDATES');
    var getLastAppt = function (x) {
        return appointmentIndex ? findLastAppointment(x, appointmentIndex) : undefined;
    };
    var membersArr = [];
    apptData === null || apptData === void 0 ? void 0 : apptData.forEach(function (e) {
        var _a;
        membersArr.push(((_a = e.member) === null || _a === void 0 ? void 0 : _a.id) || '');
    });
    var _b = useAppointmentsMeasures(membersArr, true), measures = _b.data, isMeasuresLoading = _b.isLoading;
    var convertRiskToNumber = function (questionnaire) {
        if (questionnaire.cached_results.acuity === 'severe')
            return 3;
        if (questionnaire.cached_results.risk === 'Medium')
            return 1;
        if (questionnaire.cached_results.risk === 'High')
            return 2;
        return 0;
    };
    var cols = [
        {
            Header: 'Name',
            accessor: function (row) { var _a; return (_a = row === null || row === void 0 ? void 0 : row.member) === null || _a === void 0 ? void 0 : _a.last_name; },
            Cell: function (_a) {
                var row = _a.row;
                var member = ((row === null || row === void 0 ? void 0 : row.original) || {}).member;
                var age = getAgeFromFormattedDob((member === null || member === void 0 ? void 0 : member.date_of_birth) || '');
                return (_jsx(Box, __assign({ m: 2, maxWidth: "260px" }, { children: _jsxs(TableCellLink, __assign({ "data-testid": member === null || member === void 0 ? void 0 : member.first_name, to: "/client/".concat(member === null || member === void 0 ? void 0 : member.id, "/demographics"), subtitle: "".concat(member === null || member === void 0 ? void 0 : member.date_of_birth, " (").concat(age, " yo)") }, { children: [member === null || member === void 0 ? void 0 : member.first_name, " ", member === null || member === void 0 ? void 0 : member.last_name, ' '] })) })));
            },
        },
        {
            Header: 'Appointment date',
            id: 'apptDate',
            isSorted: true,
            accessor: function (row) { return row === null || row === void 0 ? void 0 : row.start_at; },
            Cell: function (_a) {
                var _b, _c, _d;
                var row = _a.row;
                return (_jsx(Box, __assign({ m: 2 }, { children: _jsx(TableCellLink, __assign({ "data-testid": "appointmentDate", to: notesIndexedByAppointmentId
                            ? linkToNote(row.original.id, notesIndexedByAppointmentId)
                            : linkToNoteOrAppointment(row.original.id, (_b = row.original.note) === null || _b === void 0 ? void 0 : _b.id), subtitle: appointmentKindLabels[(_c = row.original) === null || _c === void 0 ? void 0 : _c.kind] }, { children: formatFullDateTime((_d = row === null || row === void 0 ? void 0 : row.original) === null || _d === void 0 ? void 0 : _d.start_at, true) })) })));
            },
        },
        {
            Header: 'Last appointment',
            accessor: function (row) { var _a, _b; return appointmentIndex ? (_a = getLastAppt(row)) === null || _a === void 0 ? void 0 : _a.start_at : (_b = row.last_appointment) === null || _b === void 0 ? void 0 : _b.start_at; },
            Cell: function (_a) {
                var row = _a.row;
                var lastAppt = appointmentIndex
                    ? getLastAppt(row.original)
                    : row.original.last_appointment;
                if (!lastAppt)
                    return _jsx(_Fragment, {});
                var note = notesIndexedByAppointmentId
                    ? notesIndexedByAppointmentId[lastAppt === null || lastAppt === void 0 ? void 0 : lastAppt.id]
                    : lastAppt === null || lastAppt === void 0 ? void 0 : lastAppt.note;
                // AP: encapsulate this elsewhere if you reuse it.
                var noteStatus = (note === null || note === void 0 ? void 0 : note.signedAt) ? 'Signed' : (note === null || note === void 0 ? void 0 : note.updatedAt) ? 'Draft' : 'Not started';
                return (_jsx(Box, __assign({ m: 2 }, { children: _jsx(TableCellLink, __assign({ to: notesIndexedByAppointmentId
                            ? linkToNote(lastAppt.id, notesIndexedByAppointmentId)
                            : linkToNoteOrAppointment(lastAppt.id, note === null || note === void 0 ? void 0 : note.id), subtitle: "Note status: ".concat(noteStatus) }, { children: formatFullDateTime(lastAppt.start_at, true) })) })));
            },
        },
        {
            Header: 'Measures',
            headerIcon: Info,
            headerTooltipTitle: 'Measures',
            headerTooltipDescription: 'Measures are clinical scales administered to clients to identify potential mental health conditions. Measures with medium or high levels are highlighted here.',
            accessor: function (row) {
                var _a;
                if (!measures)
                    return 0;
                var assessments = measures.assessments;
                var matchedAssessment = assessments.find(function (assessment) { var _a; return assessment.member_id === ((_a = row.member) === null || _a === void 0 ? void 0 : _a.id); });
                var max = Math.max.apply(Math, ((_a = matchedAssessment === null || matchedAssessment === void 0 ? void 0 : matchedAssessment.questionnaires.map(function (questionnaire) {
                    return convertRiskToNumber(questionnaire);
                })) !== null && _a !== void 0 ? _a : [0]));
                return max;
            },
            id: 'measures',
            Cell: function (_a) {
                var _b;
                var row = _a.row;
                if (!measures)
                    return _jsx(Spinner, {});
                // match member in row to assessments fetch
                var assessments = measures.assessments;
                var matchedAssessment = assessments.find(function (assessment) { var _a; return assessment.member_id === ((_a = row.original.member) === null || _a === void 0 ? void 0 : _a.id); });
                // if more than 3, truncate array to be mapped then return remainder as separate icon
                var filteredQuestionnaires = matchedAssessment === null || matchedAssessment === void 0 ? void 0 : matchedAssessment.questionnaires.filter(function (questionnaire) {
                    return questionnaire.cached_results.risk === 'Medium' ||
                        questionnaire.cached_results.risk === 'High' ||
                        (!questionnaire.cached_results.risk && questionnaire.cached_results.positive);
                }).sort(function (a, b) {
                    return convertRiskToNumber(b) - convertRiskToNumber(a);
                });
                var remainder = ((filteredQuestionnaires === null || filteredQuestionnaires === void 0 ? void 0 : filteredQuestionnaires.length) || 0) - 3;
                var truncatedMatchedAssessment = filteredQuestionnaires === null || filteredQuestionnaires === void 0 ? void 0 : filteredQuestionnaires.slice(0, 3);
                return (_jsx(Box, __assign({ m: 2 }, { children: _jsxs(TableCellLink, __assign({ to: linkToMemberMeasures((_b = matchedAssessment === null || matchedAssessment === void 0 ? void 0 : matchedAssessment.member_id) !== null && _b !== void 0 ? _b : ''), subtitle: matchedAssessment
                            ? "".concat(formatCalendarDate(matchedAssessment.ended_at), " (").concat(differenceInDays(new Date(), new Date(matchedAssessment.ended_at)), " days ago)")
                            : '' }, { children: [!!(filteredQuestionnaires === null || filteredQuestionnaires === void 0 ? void 0 : filteredQuestionnaires.length) && (_jsx(Flex, { children: _jsxs(HStack, __assign({ spacing: 1 }, { children: [truncatedMatchedAssessment === null || truncatedMatchedAssessment === void 0 ? void 0 : truncatedMatchedAssessment.map(function (questionnaire, key) {
                                            var _a;
                                            var risk = assessmentRiskRender[questionnaire.cached_results.risk];
                                            var variant = (questionnaire.cached_results.acuity === 'severe' &&
                                                AssessmentPositiveNegativeKinds.includes(questionnaire.kind)) ||
                                                !('score' in ((_a = questionnaire.cached_results) !== null && _a !== void 0 ? _a : {}))
                                                ? 'high-emphasis'
                                                : risk.emphasis;
                                            return (_jsx(VBadge, __assign({ colorScheme: risk.colorScheme, variant: variant }, { children: questionnaire.kind }), key));
                                        }), remainder > 0 && _jsxs(VBadge, { children: ["+", remainder] })] })) })), !(filteredQuestionnaires === null || filteredQuestionnaires === void 0 ? void 0 : filteredQuestionnaires.length) && 'No elevated levels'] })) })));
            },
        },
        {
            Header: 'Provider Reported',
            headerIcon: Info,
            headerTooltipTitle: 'Provider Reported',
            headerTooltipDescription: 'Risk level is reported by you and is based on the highest risk level from your last appointment, whether it’s self-harm or harm to others.',
            accessor: '',
            id: 'risk',
            Cell: function (_a) {
                var _b, _c;
                var row = _a.row;
                var _d = useFlaggedRiskConcerns(row.original.id, appointments, notesIndexedByAppointmentId, row.original.start_at, (_b = row.original.member) === null || _b === void 0 ? void 0 : _b.id, row.original.riskConcerns, row.original.last_appointment), rfhToOthers = _d.rfhToOthers, rfhToSelf = _d.rfhToSelf, date = _d.date, signedAt = _d.signedAt;
                return (_jsx(RiskTableCell, __assign({ riskToSelf: rfhToSelf || undefined, riskToOthers: rfhToOthers || undefined }, { children: !signedAt ? (_jsx(NavLink, __assign({ to: notesIndexedByAppointmentId
                            ? linkToNote(row.original.id, notesIndexedByAppointmentId)
                            : linkToNoteOrAppointment(row.original.id, (_c = row.original.note) === null || _c === void 0 ? void 0 : _c.id) }, { children: _jsx(Text, __assign({ mt: 2, size: 'small' }, { children: formatFullDate(date !== null && date !== void 0 ? date : '') })) }))) : (_jsx(_Fragment, {})) })));
            },
        },
    ];
    // if feature flag off, remove Measures col
    if (!measuresContentFlagEnabled)
        cols.splice(3, 1);
    return (_jsx(_Fragment, { children: _jsx(StatusTable, { cols: cols, data: apptData, initialState: { sortBy: [{ id: 'apptDate', desc: false }] }, isLoading: isLoading, isError: isError, noData: _jsx(NoData, { title: "You have no upcoming appointments at this time.", icon: _jsx(NoAppointments, {}) }), maxWidth: '100%', paginated: true, pageSizeOptions: defaultPageSizeOptions, preservedTableId: "upcoming" }) }));
};
export { Upcoming };
